/**
 * AdminCorneliusSkills — Tier-2 procedural skill library
 *
 * Surfaces the procedural_skills rows Cornelius records after every
 * successful research task (citations ≥ 2). The planner consults this
 * table before re-deriving plans for queries with the same intent
 * signature.  See core/procedural_skills.py.
 */

import React, { useState, useEffect, useCallback } from 'react';

const cardStyle = (open) => ({
  background:   'var(--card-bg)',
  border:       `1px solid ${open ? 'var(--primary-color)' : 'var(--border-color)'}`,
  borderRadius: 'var(--border-radius)',
  padding:      '12px 14px',
  marginBottom: 10,
  color:        'var(--text-primary)',
  boxShadow:    'var(--shadow-sm)',
});

const inputStyle = {
  padding: '7px 12px', borderRadius: 'var(--border-radius)', border: '1px solid var(--border-color)',
  background: 'var(--surface-color)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
};

const btn = {
  padding: '7px 14px', borderRadius: 'var(--border-radius)', border: 'none',
  background: 'var(--primary-color)', color: 'var(--text-on-primary)',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

function ratioColor(r) {
  if (r >= 0.85) return 'var(--success-color)';
  if (r >= 0.5)  return 'var(--warning-color)';
  return 'var(--danger-color)';
}

function SkillRow({ s }) {
  const [open, setOpen] = useState(false);
  const ratio = parseFloat(s.success_ratio || 1);
  const plan  = s.plan_json || {};
  const subtasks = Array.isArray(plan.subtasks) ? plan.subtasks : [];

  return (
    <div style={cardStyle(open)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setOpen(o=>!o)}>
        <span style={{
          padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
          background: 'var(--surface-color)', border: '1px solid var(--border-color)',
          color: 'var(--primary-color)',
        }}>skill</span>
        <span style={{ flex: 1, color: 'var(--text-primary)', fontWeight: 500, minWidth: 0, wordBreak: 'break-word' }}>
          {s.canonical_query}
        </span>
        <span style={{ fontSize: 11, color: ratioColor(ratio), fontWeight: 600 }}>
          {Math.round(ratio*100)}% success
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          ✓{s.success_count} · ✗{s.failure_count} · ~{s.avg_subtasks} steps
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {s.last_used_at ? new Date(s.last_used_at).toLocaleDateString() : '—'}
        </span>
        <span style={{ color: 'var(--primary-color)', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>

      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
        intent signature <code>{s.intent_signature}</code>
        {' · '}last task <code>{s.last_task_id ? String(s.last_task_id).slice(0,8) : '—'}</code>
        {' · '}created {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
      </div>

      {open && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--border-color)', paddingTop: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
            Plan template (intent: <em>{plan.intent_summary || '—'}</em>)
          </div>
          {subtasks.length === 0 && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No subtasks recorded.</div>
          )}
          {subtasks.map((st, i) => (
            <div key={i} style={{
              borderLeft: '2px solid var(--primary-color)', paddingLeft: 10, marginBottom: 6, fontSize: 12,
            }}>
              <div style={{ color: 'var(--text-primary)' }}>
                <strong>#{st.id ?? i+1}</strong> {st.goal}
                {st.tool && <code style={{ marginLeft: 8, color: 'var(--primary-color)' }}>{st.tool}</code>}
              </div>
              {Array.isArray(st.depends_on) && st.depends_on.length > 0 && (
                <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                  depends on: {st.depends_on.join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminCorneliusSkills() {
  const [userId, setUserId]  = useState('13');
  const [rows,   setRows]    = useState([]);
  const [loading,setLoading] = useState(false);
  const [error,  setError]   = useState('');

  const fetchRows = useCallback(async () => {
    if (!userId.trim()) { setRows([]); return; }
    setLoading(true); setError('');
    try {
      const r = await fetch(`/api/opo/cornelius-memories/skills?user_id=${userId.trim()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data.skills || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
        Procedural skills Cornelius has learnt from successful research tasks
        (≥ 2 citations). The planner reuses these as reference plans when the
        same kind of question comes up again — Voyager / ExpeL style.
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={userId} onChange={e=>setUserId(e.target.value)} onKeyDown={e=>e.key==='Enter'&&fetchRows()}
               placeholder="User ID" style={{ ...inputStyle, width: 100 }} />
        <button onClick={fetchRows} style={btn}>Refresh</button>
      </div>

      {error   && <div style={{ color: 'var(--danger-color)', fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          No skills recorded yet. Each successful research task with ≥ 2 citations
          becomes a reusable skill keyed by intent signature.
        </div>
      )}

      {rows.map(s => <SkillRow key={s.id} s={s} />)}
    </div>
  );
}
